import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import Person from '../assets/person.svg?react'
import Document from '../assets/document.svg?react'
import CheckmarkCircle from '../assets/checkmark.circle.svg?react'
import XMarkCircle from '../assets/xmark.circle.svg?react'
import Flag from '../assets/flag.svg?react'
import Plus from '../assets/plus.svg?react'
import Star from '../assets/star.outline.svg?react'
import PaperPlane from '../assets/paperplane.svg?react'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { UserAvatar } from './UserAvatar'

type NavItem =
  | 'profile'
  | 'submissions'
  | 'approved'
  | 'denied'
  | 'reports'
  | 'metadata'
  | 'blog'
  | 'myfragrances'
  | 'myreviews'

interface IDashboardMenuProps {
  isAdmin: boolean
  activeView: NavItem
  onNavigate: (view: NavItem) => void
}

export const DashboardMenu = (props: IDashboardMenuProps) => {
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // 768px is the md breakpoint
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleAvatarClick = () => {
    navigate('/dashboard')
  }

  // On desktop, render a simple button that navigates
  if (!isMobile) {
    return <UserAvatar onClick={handleAvatarClick} />
  }

  // On mobile, render the dropdown menu
  return (
    <Menu>
      <MenuButton as="div">
        <UserAvatar />
      </MenuButton>

      <MenuItems
        anchor="bottom start"
        className={
          'mt-2 bg-white border border-searchBorder rounded-lg shadow-lg p-2 min-w-[200px] z-50'
        }
      >
        <MenuItem>
          {({ focus }) => (
            <button
              onClick={() => props.onNavigate('profile')}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                } ${props.activeView === 'profile' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
            >
              <Person width={18} height={18} />
              <span className={'text-sm'}>Profile</span>
            </button>
          )}
        </MenuItem>

        <MenuItem>
          {({ focus }) => (
            <button
              onClick={() => navigate('/submit')}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                }`}
            >
              <PaperPlane width={18} height={18} />
              <span className={'text-sm'}>Submit</span>
            </button>
          )}
        </MenuItem>

        {props.isAdmin && (
          <>
            <MenuItem>
              {({ focus }) => (
                <button
                  onClick={() => props.onNavigate('submissions')}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                    } ${props.activeView === 'submissions' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
                >
                  <Document width={18} height={18} />
                  <span className={'text-sm'}>Submissions</span>
                </button>
              )}
            </MenuItem>

            <MenuItem>
              {({ focus }) => (
                <button
                  onClick={() => props.onNavigate('approved')}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                    } ${props.activeView === 'approved' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
                >
                  <CheckmarkCircle width={18} height={18} />
                  <span className={'text-sm'}>Approved submissions</span>
                </button>
              )}
            </MenuItem>

            <MenuItem>
              {({ focus }) => (
                <button
                  onClick={() => props.onNavigate('denied')}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                    } ${props.activeView === 'denied' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
                >
                  <XMarkCircle width={18} height={18} />
                  <span className={'text-sm'}>Denied submissions</span>
                </button>
              )}
            </MenuItem>

            <MenuItem>
              {({ focus }) => (
                <button
                  onClick={() => props.onNavigate('reports')}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                    } ${props.activeView === 'reports' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
                >
                  <Flag width={18} height={18} />
                  <span className={'text-sm'}>Reports</span>
                </button>
              )}
            </MenuItem>

            <MenuItem>
              {({ focus }) => (
                <button
                  onClick={() => props.onNavigate('metadata')}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                    } ${props.activeView === 'metadata' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
                >
                  <Document width={18} height={18} />
                  <span className={'text-sm'}>Metadata</span>
                </button>
              )}
            </MenuItem>

            <MenuItem>
              {({ focus }) => (
                <button
                  onClick={() => props.onNavigate('blog')}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                    } ${props.activeView === 'blog' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
                >
                  <Document width={18} height={18} />
                  <span className={'text-sm'}>Blog Posts</span>
                </button>
              )}
            </MenuItem>
          </>
        )}

        <MenuItem>
          {({ focus }) => (
            <button
              onClick={() => props.onNavigate('myfragrances')}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                } ${props.activeView === 'myfragrances' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
            >
              <Plus width={18} height={18} />
              <span className={'text-sm'}>My fragrances</span>
            </button>
          )}
        </MenuItem>

        <MenuItem>
          {({ focus }) => (
            <button
              onClick={() => props.onNavigate('myreviews')}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${focus ? 'bg-navHover' : ''
                } ${props.activeView === 'myreviews' ? 'bg-navHover text-primaryBlue font-semibold' : ''}`}
            >
              <Star width={18} height={18} />
              <span className={'text-sm'}>My reviews</span>
            </button>
          )}
        </MenuItem>
      </MenuItems>
    </Menu>
  )
}
